import React from 'react';
import { Box, Avatar, Badge, useTheme } from '@mui/material';
import { 
  Star, 
  Diamond, 
  EmojiEvents, 
  Whatshot,
  Shield,
  Verified,
  WorkspacePremium,
  LocalFireDepartment,
  Psychology,
  TrendingUp,
  AutoAwesome,
  MilitaryTech,
  Movie,
  Theaters,
  VideoLibrary,
  LocalMovies,
  Slideshow,
  PlayCircle,
  MovieCreation,
  Videocam,
  CameraRoll,
  LiveTv,
  OndemandVideo,
  TheaterComedy,
  Masks,
  Mic,
  RecordVoiceOver,
  Album,
  QueueMusic,
  LibraryMusic,
  MusicVideo,
  Radio,
  Headphones,
  VolumeUp,
  SportsKabaddi,
  SportsCricket,
  SportsFootball,
  SportsBasketball,
  SportsMotorsports,
  DirectionsBike,
  DirectionsRun,
  LocalActivity,
  Agriculture,
  WbSunny,
  NightsStay,
  Waves,
  LocationCity,
  Home,
  Face,
  SentimentSatisfied,
  SentimentVerySatisfied,
  Mood,
  EmojiEmotions
} from '@mui/icons-material';
import S3Avatar from './S3Avatar';

const ICON_MAP = {
  Star: Star,
  Diamond: Diamond,
  EmojiEvents: EmojiEvents,
  Whatshot: Whatshot,
  Shield: Shield,
  Verified: Verified,
  WorkspacePremium: WorkspacePremium,
  LocalFireDepartment: LocalFireDepartment,
  Psychology: Psychology,
  TrendingUp: TrendingUp,
  AutoAwesome: AutoAwesome,
  MilitaryTech: MilitaryTech,
  Movie: Movie,
  Theaters: Theaters,
  VideoLibrary: VideoLibrary,
  LocalMovies: LocalMovies,
  Slideshow: Slideshow,
  PlayCircle: PlayCircle,
  MovieCreation: MovieCreation,
  Videocam: Videocam,
  CameraRoll: CameraRoll,
  LiveTv: LiveTv,
  OndemandVideo: OndemandVideo,
  TheaterComedy: TheaterComedy,
  Masks: Masks,
  Mic: Mic,
  RecordVoiceOver: RecordVoiceOver,
  Album: Album,
  QueueMusic: QueueMusic,
  LibraryMusic: LibraryMusic,
  MusicVideo: MusicVideo,
  Radio: Radio,
  Headphones: Headphones,
  VolumeUp: VolumeUp,
  SportsKabaddi: SportsKabaddi,
  SportsCricket: SportsCricket,
  SportsFootball: SportsFootball,
  SportsBasketball: SportsBasketball,
  SportsMotorsports: SportsMotorsports,
  DirectionsBike: DirectionsBike,
  DirectionsRun: DirectionsRun,
  LocalActivity: LocalActivity,
  Agriculture: Agriculture,
  WbSunny: WbSunny,
  NightsStay: NightsStay,
  Waves: Waves,
  LocationCity: LocationCity,
  Home: Home,
  Face: Face,
  SentimentSatisfied: SentimentSatisfied,
  SentimentVerySatisfied: SentimentVerySatisfied,
  Mood: Mood,
  EmojiEmotions: EmojiEmotions
};

const BadgedAvatar = ({ 
  user, 
  badges = [], 
  size = 40, 
  sx = {},
  showBadge = true,
  badgeOrder = null,
  showBadges = true,
  ...props 
}) => {
  const theme = useTheme();
  
  // Get primary badge based on user's badge display settings
  const getPrimaryBadge = () => {
    if (!badges || badges.length === 0) return null;
    
    // Check if user has badge display settings
    let displaySettings = null;
    try {
      displaySettings = user?.badgeDisplaySettings ? 
        (typeof user.badgeDisplaySettings === 'string' ? 
          JSON.parse(user.badgeDisplaySettings) : 
          user.badgeDisplaySettings) : null;
    } catch (error) {
      console.error('Error parsing badge display settings:', error);
    }
    
    // If user has disabled badge display (check prop first, then settings)
    if (!showBadges || (displaySettings && displaySettings.showBadges === false)) {
      return null;
    }
    
    // Filter badges by visibility settings
    let visibleBadges = badges;
    if (displaySettings?.visibleBadges && displaySettings.visibleBadges.length > 0) {
      visibleBadges = badges.filter(badge => 
        displaySettings.visibleBadges.includes(badge.badgeType) ||
        displaySettings.visibleBadges.includes(badge.type) ||
        displaySettings.visibleBadges.includes(badge.id)
      );
      if (visibleBadges.length === 0) return null;
    }
    
    // Use badge order from props first, then settings
    const orderToUse = badgeOrder || displaySettings?.badgeOrder;
    if (orderToUse && orderToUse.length > 0) {
      const orderedBadge = visibleBadges.find(badge => 
        badge.badgeType === orderToUse[0] ||
        badge.type === orderToUse[0] ||
        badge.id === orderToUse[0]
      );
      if (orderedBadge) return orderedBadge;
    }
    
    // Use activeBadges from user if available
    if (user?.activeBadges && user.activeBadges.length > 0) {
      const activeBadge = visibleBadges.find(badge => 
        badge.badgeType === user.activeBadges[0] ||
        badge.type === user.activeBadges[0] ||
        badge.id === user.activeBadges[0]
      );
      if (activeBadge) return activeBadge;
    }
    
    // Priority order: PRO > other badges > ACHIEVEMENT (fallback)
    const proBadge = visibleBadges.find(badge => badge.badgeType === 'PRO' || badge.type === 'PRO');
    if (proBadge) return proBadge;
    
    const nonAchievementBadges = visibleBadges.filter(badge => 
      badge.badgeType !== 'ACHIEVEMENT' && badge.type !== 'ACHIEVEMENT'
    );
    if (nonAchievementBadges.length > 0) return nonAchievementBadges[0];
    
    // Fallback to first visible badge
    return visibleBadges[0];
  };
  
  const primaryBadge = getPrimaryBadge();
  const BadgeIcon = primaryBadge ? ICON_MAP[primaryBadge.icon] || Star : null;

  const avatarElement = user?.avatar ? (
    <S3Avatar
      s3Key={user.avatar}
      targetIdentityId={user.identityId}
      accessLevel="protected"
      sx={{ width: size, height: size, ...sx }}
      {...props}
    >
      {user.displayName?.[0]?.toUpperCase()}
    </S3Avatar>
  ) : (
    <Avatar sx={{ width: size, height: size, ...sx }} {...props}>
      {user?.displayName?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || '?'}
    </Avatar>
  );

  if (!showBadge || !primaryBadge || !BadgeIcon) {
    return avatarElement;
  }

  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <Box
          sx={{
            width: size * 0.3,
            height: size * 0.3,
            borderRadius: '50%',
            bgcolor: primaryBadge.color || (theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 2px 8px rgba(0, 0, 0, 0.4)' 
              : '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          <BadgeIcon 
            sx={{ 
              fontSize: size * 0.2, 
              color: theme.palette.mode === 'dark' ? 'black' : 'white'
            }} 
          />
        </Box>
      }
    >
      {avatarElement}
    </Badge>
  );
};

export default BadgedAvatar;